import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${API_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user');
      if (window.location.pathname.startsWith('/staff')) {
        window.location.href = '/staff/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth
export const authAPI = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  register: (data) => api.post('/auth/register', data),
  getMe: () => api.get('/auth/me'),
};

// Categories
export const categoriesAPI = {
  getAll: () => api.get('/categories'),
  getBySlug: (slug) => api.get(`/categories/${slug}`),
  create: (data) => api.post('/categories', data),
};

// Tags
export const tagsAPI = {
  getAll: () => api.get('/tags'),
  create: (data) => api.post('/tags', data),
};

// Homepage (optimized single call)
export const homepageAPI = {
  getData: () => api.get('/homepage'),
};

// Articles
export const articlesAPI = {
  getAll: (params) => api.get('/articles', { params }),
  getPublished: (params) => api.get('/articles/published', { params }),
  getFeatured: (limit = 6) => api.get(`/articles/featured?limit=${limit}`),
  getHeadline: () => api.get('/articles/headline'),
  setHeadline: (id) => api.put(`/articles/${id}/set-headline`),
  getPopular: (limit = 10) => api.get(`/articles/popular?limit=${limit}`),
  getById: (id) => api.get(`/articles/${id}`),
  getBySlug: (slug) => api.get(`/articles/by-slug/${slug}`),
  create: (data) => api.post('/articles', data),
  update: (id, data) => api.put(`/articles/${id}`, data),
  delete: (id) => api.delete(`/articles/${id}`),
  recordView: (id) => api.post(`/articles/${id}/view`),
};

// Trending Videos
export const trendingVideosAPI = {
  getAll: () => api.get('/trending-videos'),
  getAllAdmin: () => api.get('/trending-videos/all'),
  create: (data) => api.post('/trending-videos', data),
  update: (id, data) => api.put(`/trending-videos/${id}`, data),
  updateBySlot: (slot, data) => api.put(`/trending-videos/slot/${slot}`, data),
  delete: (id) => api.delete(`/trending-videos/${id}`),
};

// Media
export const mediaAPI = {
  upload: (formData) => api.post('/media/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  getAll: (params) => api.get('/media', { params }),
  delete: (id) => api.delete(`/media/${id}`),
};

// Submissions
export const submissionsAPI = {
  create: (data) => api.post('/submissions', data),
  getAll: (params) => api.get('/submissions', { params }),
  getById: (id) => api.get(`/submissions/${id}`),
  markReviewed: (id) => api.put(`/submissions/${id}/review`),
  convert: (id) => api.post(`/submissions/${id}/convert`),
};

// Search
export const searchAPI = {
  search: (q, limit = 20) => api.get('/search', { params: { q, limit } }),
};

// Init
export const initAPI = {
  init: () => api.post('/init'),
};

// Newsletter
export const newsletterAPI = {
  subscribe: (data) => api.post('/newsletter/subscribe', data),
  getSubscribers: (params) => api.get('/newsletter/subscribers', { params }),
  getStats: () => api.get('/newsletter/stats'),
  deleteSubscriber: (id) => api.delete(`/newsletter/subscribers/${id}`),
  export: () => api.get('/newsletter/export'),
  broadcast: (data) => api.post('/newsletter/broadcast', data),
  // Templates
  getTemplates: () => api.get('/newsletter/templates'),
  getWeeklyDigest: () => api.get('/newsletter/templates/weekly-digest'),
  getBreakingNews: (articleId) => api.get('/newsletter/templates/breaking-news', { params: { article_id: articleId } }),
  getCategorySpotlight: (categorySlug) => api.get('/newsletter/templates/category-spotlight', { params: { category_slug: categorySlug } }),
  getNewArticle: (articleId) => api.get('/newsletter/templates/new-article', { params: { article_id: articleId } }),
  sendTemplate: (templateType, articleId, categorySlug) => api.post('/newsletter/send-template', null, { 
    params: { template_type: templateType, article_id: articleId, category_slug: categorySlug } 
  }),
};

// Editorial Notes
export const notesAPI = {
  getForArticle: (articleId) => api.get(`/articles/${articleId}/notes`),
  create: (articleId, content) => api.post(`/articles/${articleId}/notes`, { article_id: articleId, content }),
  delete: (articleId, noteId) => api.delete(`/articles/${articleId}/notes/${noteId}`),
};

// Analytics
export const analyticsAPI = {
  recordPageView: (path, referrer) => api.post('/analytics/pageview', null, { params: { path, referrer } }),
  getOverview: (days = 30) => api.get('/analytics/overview', { params: { days } }),
  getArticles: (days = 30, limit = 20) => api.get('/analytics/articles', { params: { days, limit } }),
  getCategories: (days = 30) => api.get('/analytics/categories', { params: { days } }),
  getTraffic: (days = 30) => api.get('/analytics/traffic', { params: { days } }),
};

// Site Settings
export const settingsAPI = {
  get: () => api.get('/settings'),
  update: (data) => api.put('/settings', data),
};

// Audit Logs
export const auditLogsAPI = {
  getAll: (params) => api.get('/audit-logs', { params }),
  getStats: (days = 30) => api.get('/audit-logs/stats', { params: { days } }),
};

// Comments
export const commentsAPI = {
  create: (data) => api.post('/comments', data),
  getForArticle: (articleId, includePending = false) => 
    api.get(`/comments/article/${articleId}`, { params: { include_pending: includePending } }),
  getAll: (params) => api.get('/comments', { params }),
  getStats: () => api.get('/comments/stats'),
  update: (id, data) => api.put(`/comments/${id}`, data),
  delete: (id) => api.delete(`/comments/${id}`),
  bulkAction: (commentIds, action) => api.post('/comments/bulk-action', null, { params: { comment_ids: commentIds, action } }),
};

// Bulk Actions
export const bulkAPI = {
  articles: (articleIds, action) => api.post('/articles/bulk-action', null, { 
    params: { article_ids: articleIds, action } 
  }),
};

export default api;
